import { logout as logoutRequest } from '@/api/adapters/auth';
import { ApiError } from '@/api/error';
import { queryClient } from '@/app/providers/queryClient';
import { useAuthStore } from '@/app/store/auth.store';

/**
 * Hook returning a `logout` function that performs the full FE logout
 * sequence (see `docs/api-contracts/login.md §2.2`):
 *
 *   1. POST /medical-staff/auth/logout
 *   2. Clear the auth store
 *   3. Clear the React Query cache (drop any data from the previous session)
 *
 * On a genuine server error (not 401) the local session is left intact and the
 * error is re-thrown BEFORE any cleanup, so the user stays logged in on the
 * current page and can retry (SHCT-318). `UNAUTHENTICATED` (the session was
 * already gone) is treated as success — local cleanup runs and it resolves so
 * the caller redirects. The caller is responsible for navigating on success.
 */
export function useLogout(): () => Promise<void> {
  const localLogout = useAuthStore((s) => s.logout);

  return async () => {
    try {
      await logoutRequest();
    } catch (err) {
      const alreadyGone = err instanceof ApiError && err.code === 'UNAUTHENTICATED';
      // Server logout failed for a real reason — keep the local session so the
      // user isn't silently logged out and can retry from the same page.
      if (!alreadyGone) throw err;
    }
    localLogout();
    queryClient.clear();
  };
}
